'use client';

import { cn } from '@coas/ui/lib';
import Image from 'next/image';
import Link from 'next/link';
import { type ComponentRef, forwardRef, memo } from 'react';
import { useLocale } from 'next-intl';
import { pickLocalized, type Locale } from '@coas/db/utils/i18n';
import { usePreviousDistinct } from 'react-use';
import { api } from 'trpc/react';
import type { RouterOutputs } from 'trpc/shared';
import { imgLoader } from '~/shared/lib';
import styles from './SubCategories.module.scss';

type SubCategoriesProps = {
    category: RouterOutputs['category']['getBySlug'];
    className?: string;
};

export default memo(
    forwardRef<ComponentRef<'div'>, SubCategoriesProps>(({ category, className }, ref) => {
        const locale = useLocale() as Locale;
        const { isLoading, data } = api.category.getSubOrSiblingCategories.useQuery([category.sex, category.item.slug]);

        const prevData = usePreviousDistinct(data);

        return (
            <div
                ref={ref}
                className={cn(
                    'relative w-full overflow-hidden transition-all duration-300',
                    (isLoading ? prevData : data)?.items.length ? 'h-28 pt-page-1/2' : 'h-0',
                    className,
                )}
            >
                <div
                    className={cn(
                        'absolute inset-0 top-page-1/2 mx-auto flex w-fit max-w-[min(var(--grid-width),100%)] items-stretch overflow-auto child:px-3 px-5',
                        styles.scrollable,
                    )}
                >
                    {(isLoading ? prevData : data)?.items.map(subcategory => (
                        <Link
                            key={subcategory.id}
                            title={pickLocalized(subcategory, 'name', locale)}
                            href={{ pathname: '/products', query: { category: subcategory.slug } }}
                            className='group flex w-24 shrink-0 flex-col items-stretch justify-evenly outline-none'
                        >
                            <div
                                className={cn(
                                    'size-14 self-center overflow-hidden rounded-full bg-neutral-200',
                                    'group-focus-visible:ring-2 border-2',
                                    subcategory.id === category.item.id
                                        ? 'group-focus-visible:ring-blue-400/40 border-blue-400'
                                        : 'group-focus-visible:ring-neutral-300/60 border-neutral-400/60',
                                )}
                            >
                                {!!subcategory.image && (
                                    <Image
                                        loader={imgLoader({ src: subcategory.image.key, height: 160, width: 160 })}
                                        src={subcategory.image.key}
                                        placeholder='blur'
                                        blurDataURL={subcategory.image.lqip ?? undefined}
                                        alt={subcategory.image.alt}
                                        width={subcategory.image.width ?? undefined}
                                        height={subcategory.image.height ?? undefined}
                                        className='h-full w-full object-cover object-center'
                                    />
                                )}
                            </div>

                            <span className='truncate text-center text-12 text-neutral-600'>{pickLocalized(subcategory, 'name', locale)}</span>
                        </Link>
                    ))}
                </div>
            </div>
        );
    }),
);
